import { useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { DownloadIcon } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@ems/ui/components/alert'
import { Button } from '@ems/ui/components/button'
import { Input } from '@ems/ui/components/input'
import { Field } from '@ems/ui/patterns/field'
import { PageHeader } from '@ems/ui/patterns/page-header'

import { OptionSelect } from '@ems/ui/patterns/option-select'
import {
  describeFailure,
  IMPORT_OPTIONAL_COLUMNS,
  IMPORT_REQUIRED_COLUMNS,
  type BulkImportResult,
  type ImportKind,
} from '#admin/features/registrations/api.ts'
import { useBulkImport } from '#admin/features/registrations/mutations.ts'
import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { formSettingsQuery } from '#admin/features/registration-form/queries.ts'
import { ImportRegistrationsSkeleton } from '#admin/features/registrations/registration-skeletons.tsx'

/**
 * `/ticketing/registrations/import` — a spreadsheet of delegates, in one go.
 *
 * A page rather than a dialog, because **the results are the point**: the API answers 200 with
 * every rejected line and why, so the operator fixes three rows instead of re-running four
 * hundred. A dialog that closed on success would throw that list away, which is what v1 did.
 */
export const Route = createFileRoute('/_authenticated/ticketing/registrations/import')({
  pendingComponent: ImportRegistrationsSkeleton,
  staticData: { crumb: 'Import' },
  // A write-only page: without `ticket.manage` there is nothing it can do, so it is not offered.
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery())
    if (!can(session, 'ticket.manage')) throw notFound()
  },
  notFoundComponent: NotFoundView,
  loader: ({ context }) => context.queryClient.ensureQueryData(formSettingsQuery()),
  component: ImportPage,
})

const KINDS = [
  {
    value: 'priced',
    label: 'Priced registrations',
    help: 'Each delegation is priced from its ticket types and completed as paid, invoiced outside the system.',
  },
  {
    value: 'exhibitor-pass',
    label: 'Complimentary exhibitor passes',
    help: 'Each delegation is issued free passes. The ticket type must have no price.',
  },
] as const satisfies readonly { value: ImportKind; label: string; help: string }[]

/** A header cell, quoted when it needs to be — a question's label can hold a comma. */
const cell = (text: string) => (/[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text)

function ImportPage() {
  const { data: settings } = useSuspenseQuery(formSettingsQuery())
  // The edition's own questions join the template by column name, so an operator who fills it in
  // gets every question the form asks. File questions are left out: a spreadsheet cannot carry one.
  const questions = settings.fields.filter((field) => !field.archived && field.type !== 'file')
  const header = [
    ...IMPORT_REQUIRED_COLUMNS,
    ...IMPORT_OPTIONAL_COLUMNS,
    ...questions.map((q) => q.key),
  ]
    .map(cell)
    .join(',')
  const template = `data:text/csv;charset=utf-8,${encodeURIComponent(`${header}\n`)}`

  const [kind, setKind] = useState<ImportKind>('priced')
  const [file, setFile] = useState<{ name: string; csv: string } | null>(null)
  const [result, setResult] = useState<BulkImportResult | null>(null)
  const upload = useBulkImport()

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Import registrations"
        description="One row per delegate. Rows sharing a contact email become one registration."
        actions={
          <Button asChild variant="outline">
            <a href={template} download="registrations-template.csv">
              <DownloadIcon />
              Download template
            </a>
          </Button>
        }
      />

      <form
        className="bg-card flex max-w-2xl flex-col gap-4 rounded-lg border border-(--border) p-4 md:p-6"
        onSubmit={(event) => {
          event.preventDefault()
          if (file === null || upload.isPending) return
          setResult(null)
          upload.mutate({ kind, csv: file.csv }, { onSuccess: setResult })
        }}
      >
        <Field label="What to import" help={KINDS.find((k) => k.value === kind)?.help}>
          {(props) => (
            <OptionSelect
              {...props}
              value={kind}
              onValueChange={(value) => setKind(value === 'exhibitor-pass' ? value : 'priced')}
              options={KINDS}
            />
          )}
        </Field>

        <Field
          label="Spreadsheet (CSV)"
          required
          help={
            <>
              Required columns: <code>{IMPORT_REQUIRED_COLUMNS.join(', ')}</code>. Optional:{' '}
              <code>{IMPORT_OPTIONAL_COLUMNS.join(', ')}</code>
              {questions.length === 0 ? null : (
                <>
                  , and this edition’s questions by column name or by label (
                  <code>{questions.map((q) => q.key).join(', ')}</code>)
                </>
              )}
              . Header case and punctuation do not matter — <code>Contact Email</code> and{' '}
              <code>contact_email</code> both work. Phones without a <code>+</code> are read as
              numbers in {settings.defaultCountry}.
            </>
          }
        >
          {(props) => (
            <Input
              {...props}
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                const picked = event.target.files?.[0]
                setResult(null)
                setFile(null)
                void picked?.text().then((csv) => setFile({ name: picked.name, csv }))
              }}
            />
          )}
        </Field>

        <Button type="submit" className="self-start" disabled={file === null || upload.isPending}>
          {upload.isPending ? 'Importing…' : 'Import'}
        </Button>
      </form>

      {result === null ? null : <Results result={result} fileName={file?.name ?? 'the file'} />}
    </div>
  )
}

function Results({ result, fileName }: { result: BulkImportResult; fileName: string }) {
  const failed = result.failures.length > 0
  return (
    <section className="flex flex-col gap-3" aria-live="polite">
      <Alert variant={failed ? 'destructive' : 'default'}>
        <AlertTitle>
          {result.imported} seat(s) imported into {result.registrationIds.length} registration(s)
          {failed ? `, ${result.failures.length} problem(s)` : ''}
        </AlertTitle>
        <AlertDescription>
          {failed
            ? `Fix these lines in ${fileName} and import just those rows again — everything else is already in.`
            : 'Every row imported.'}{' '}
          <Link to="/ticketing/registrations" className="underline">
            See registrations
          </Link>
        </AlertDescription>
      </Alert>

      {failed ? (
        <ol className="flex flex-col gap-2 rounded-lg border border-(--border) p-4 text-sm">
          {result.failures.map((failure) => (
            <li key={`${failure.lines.join('-')}-${failure.reason}`}>{describeFailure(failure)}</li>
          ))}
        </ol>
      ) : null}

      {result.unmatchedColumns.length === 0 ? null : (
        <Alert>
          <AlertTitle>
            {result.unmatchedColumns.length === 1
              ? '1 column was not imported'
              : `${result.unmatchedColumns.length} columns were not imported`}
          </AlertTitle>
          <AlertDescription>
            {result.unmatchedColumns.join(', ')}{' '}
            {result.unmatchedColumns.length === 1 ? 'matches' : 'match'} no question on the
            registration form. Check the spelling of the header, or add the question in Settings →
            Registration form and import those rows again.
          </AlertDescription>
        </Alert>
      )}
    </section>
  )
}
